import React from 'react'
import { Helmet } from 'react-helmet'

export const HOMEPAGE_TITLE = '好好生活 - 张喆的个人网站'

export const SITE_URL = 'https://www.doublez.site'

export const PERSON_ID = `${SITE_URL}/#zhe-zhang`

export const personStructuredData = {
  '@type': 'Person',
  '@id': PERSON_ID,
  name: '张喆',
  alternateName: ['Zhe Zhang', 'Zhang Zhe', 'doubleZ', 'doubleZ0108'],
  url: `${SITE_URL}/`,
  image:
    'https://images.ctfassets.net/odsfb7g0v9wp/53QBDpRWamZhWYrqv5ZAUk/194f7bdf873966911dedc59f0d866696/hero-boya.jpeg',
  jobTitle: '算法工程师 / AI Researcher',
  alumniOf: [
    {
      '@type': 'CollegeOrUniversity',
      name: '北京大学',
      alternateName: ['Peking University', 'PKU', '北大'],
      url: 'https://www.pku.edu.cn/',
    },
    {
      '@type': 'CollegeOrUniversity',
      name: '同济大学',
      alternateName: ['Tongji University', '同济'],
      url: 'https://www.tongji.edu.cn/',
    },
  ],
  homeLocation: {
    '@type': 'Place',
    name: '吉林省通化市',
    alternateName: ['吉林·通化', 'Tonghua, Jilin'],
  },
  knowsAbout: [
    'Artificial Intelligence',
    'Algorithms',
    'AIGC',
    'Computer Vision',
    'Computer Graphics',
    'Multi-view Stereo',
    '3D Reconstruction',
    'Depth Estimation',
  ],
  sameAs: [
    'https://github.com/doubleZ0108',
    'https://scholar.google.com/citations?user=30PFXmMAAAAJ&hl=zh-CN',
    'https://www.linkedin.com/in/doubleZ0108',
    'https://www.zhihu.com/people/doubleZ0108/columns',
    'https://unsplash.com/@doublez0108',
  ],
}

const defaultDescription =
  '张喆（Zhe Zhang，亦作 Zhang Zhe，网名 doubleZ）的个人网站。北京大学硕士、同济大学学士，来自吉林通化，从事计算机、AI 研究与算法工程，专注 AIGC、计算机视觉与三维重建。'

const defaultKeywords = [
  '张喆',
  'Zhang Zhe',
  'Zhe Zhang',
  'doubleZ',
  'doubleZ0108',
  '北京大学 张喆',
  '北京大学张喆',
  '北大 张喆',
  '北大张喆',
  '同济大学 张喆',
  '同济大学张喆',
  '同济 张喆',
  '同济张喆',
  '吉林 张喆',
  '张喆 吉林',
  '通化 张喆',
]

export const absoluteUrl = path => {
  if (!path || path === '/') return `${SITE_URL}/`
  if (/^https?:\/\//.test(path)) return path
  if (path.startsWith('//')) return `https:${path}`
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return `${SITE_URL}${normalizedPath}`
}

const serializeStructuredData = data =>
  JSON.stringify(data).replace(/</g, '\\u003c')

const SEO = ({
  title = HOMEPAGE_TITLE,
  description = defaultDescription,
  pathname = '/',
  image = personStructuredData.image,
  type = 'website',
  keywords = [],
  structuredData,
  noindex = false,
  lang = 'zh-CN',
  includeDefaultKeywords = true,
  author = '张喆（Zhe Zhang / Zhang Zhe / doubleZ）',
  siteName = 'doubleZ 的个人网站',
  locale = 'zh_CN',
  alternateLocale = 'en_US',
  imageAlt = '张喆（Zhe Zhang / doubleZ）',
}) => {
  const canonicalUrl = absoluteUrl(pathname)
  const imageUrl = absoluteUrl(image)
  const robots = noindex
    ? 'noindex, nofollow'
    : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
  const baseKeywords = includeDefaultKeywords ? defaultKeywords : []
  const allKeywords = Array.from(new Set([...baseKeywords, ...keywords]))
  const jsonLd = structuredData
    ? {
        '@context': 'https://schema.org',
        '@graph': Array.isArray(structuredData)
          ? structuredData
          : [structuredData],
      }
    : null

  return (
    <Helmet htmlAttributes={{ lang }}>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={allKeywords.join(', ')} />
      <meta name="author" content={author} />
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={robots} />
      <meta name="bingbot" content={robots} />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={locale} />
      {alternateLocale && (
        <meta property="og:locale:alternate" content={alternateLocale} />
      )}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:alt" content={imageAlt} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {jsonLd && (
        <script type="application/ld+json">
          {serializeStructuredData(jsonLd)}
        </script>
      )}
    </Helmet>
  )
}

export default SEO
